import $ from 'jquery';
import BpmnViewer from 'bpmn-js/lib/NavigatedViewer';

import TextStyleRenderer from '../../modeler/app/textStyle/TextStyleRenderer';
import elizaTextStyleDescriptor from '../../modeler/app/textStyle/eliza-textstyle.json';

// Viewer-only module: nur Renderer, kein Context Pad / Popup
var textStyleViewerModule = {
  __init__: ['textStyleRenderer'],
  textStyleRenderer: ['type', TextStyleRenderer]
};

/**
 * ELIZA BPMN Viewer Wrapper
 * Modernized version mit Promise-basierter API
 */
class ElizaBpmnViewer {
  constructor(options = {}) {
    // TextStyle-Modul und moddleExtension in Optionen mergen
    var additionalModules = (options.additionalModules || []).concat([textStyleViewerModule]);
    var moddleExtensions = Object.assign({}, options.moddleExtensions || {}, {
      eliza: elizaTextStyleDescriptor
    });

    this.viewer = new BpmnViewer(Object.assign({}, options, {
      additionalModules: additionalModules,
      moddleExtensions: moddleExtensions
    }));
    this.eventBus = this.viewer.get('eventBus');
    this.elementRegistry = this.viewer.get('elementRegistry');
    this.canvas = this.viewer.get('canvas');

    console.log('[ELIZA BPMN Viewer] Initialisiert mit BPMN.js v18.7.0');
  }

  /**
   * Importiert BPMN XML (Promise-basiert)
   */
  async importXML(xml) {
    try {
      await this.viewer.importXML(xml);

      // Mobile Touch-Events für Zoom/Pan hinzufügen
      this.enableMobileControls();

      // BPMN.io Logo Click-Events unterbinden um Overlay-Problem zu vermeiden
      this.disableBpmnLogoClick();

      console.log('[ELIZA BPMN Viewer] Diagramm erfolgreich importiert');
      return { success: true };
    } catch (error) {
      console.error('[ELIZA BPMN Viewer] Import Fehler:', error);
      throw error;
    }
  }

  /**
   * Exportiert SVG (Promise-basiert)
   */
  async saveSVG(options = { format: true }) {
    try {
      const { svg } = await this.viewer.saveSVG(options);
      return { svg };
    } catch (error) {
      console.error('[ELIZA BPMN Viewer] SVG Export Fehler:', error);
      throw error;
    }
  }

  /**
   * Zoom to fit viewport
   */
  zoomToFit() {
    this.canvas.zoom('fit-viewport');
  }

  /**
   * Registriert Element Click Handler
   */
  onElementClick(callback) {
    this.eventBus.on('element.click', (e) => {
      const sequenceFlowElement = this.elementRegistry.get(e.element.id);
      const sequenceFlow = sequenceFlowElement.businessObject;

      callback({
        element: e.element,
        gfx: e.gfx,
        businessObject: sequenceFlow,
        id: e.element.id,
        name: sequenceFlow.name
      });
    });
  }

  /**
   * ELIZA-spezifische Methode für BPMN Info Abfrage
   */
  setupElizaClickHandler(diagramId) {
    this.onElementClick((data) => {
      $.ajax({
        url: "/prozesse/diagrams/get_bpmn_info",
        data: {
          bpmn_id: data.id,
          diagram_id: diagramId,
          name: data.name || ''
        },
        success: function (result) {
          $("#result").html(result);
        },
        error: function(error) {
          console.error('[ELIZA BPMN Viewer] AJAX Fehler:', error);
        }
      });
    });
  }

  /**
   * Erstelle SVG Download
   */
  async downloadSVG(filename = 'diagram.svg') {
    try {
      const { svg } = await this.saveSVG();
      const svgBlob = new Blob([svg], { type: 'image/svg+xml' });

      const downloadUrl = URL.createObjectURL(svgBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.click();

      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('[ELIZA BPMN Viewer] Download Fehler:', error);
    }
  }

  /**
   * Zugriff auf interne Viewer-Instanz
   */
  getViewer() {
    return this.viewer;
  }

  /**
   * Zugriff auf Services
   */
  get(serviceName) {
    return this.viewer.get(serviceName);
  }

  /**
   * BPMN.io Logo Click-Events unterbinden um Overlay-Problem zu vermeiden
   */
  disableBpmnLogoClick() {
    const canvasContainer = this.canvas.getContainer();

    // Funktion um Click-Events zu unterbinden
    const disableClicks = () => {
      // Logo per CSS-Klasse finden und Click-Events unterbinden
      const logoElements = canvasContainer.querySelectorAll('.bjs-powered-by');
      logoElements.forEach(element => {
        element.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[ELIZA BPMN Viewer] BPMN.io Logo Click unterbunden');
        }, true);
      });

      // Alle Links die auf bpmn.io verweisen
      const bpmnLinks = canvasContainer.querySelectorAll('a[href*="bpmn.io"]');
      bpmnLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[ELIZA BPMN Viewer] BPMN.io Link Click unterbunden');
        }, true);
      });

      // Alle Elemente mit "powered-by" im Klassennamen
      const poweredByElements = canvasContainer.querySelectorAll('[class*="powered-by"]');
      poweredByElements.forEach(element => {
        element.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[ELIZA BPMN Viewer] Powered-by Element Click unterbunden');
        }, true);
      });
    };

    // Sofort versuchen Click-Events zu unterbinden
    disableClicks();

    // MutationObserver um Click-Events bei dynamisch hinzugefügten Logos zu unterbinden
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Prüfen ob hinzugefügtes Element das Logo ist
              if (node.matches && (
                node.matches('.bjs-powered-by') ||
                node.matches('a[href*="bpmn.io"]') ||
                node.matches('[class*="powered-by"]')
              )) {
                node.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[ELIZA BPMN Viewer] BPMN.io Logo Click via MutationObserver unterbunden');
                }, true);
              } else if (node.querySelector) {
                // In Kindern suchen
                const logoInChildren = node.querySelectorAll('.bjs-powered-by, a[href*="bpmn.io"], [class*="powered-by"]');
                logoInChildren.forEach(logo => {
                  logo.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[ELIZA BPMN Viewer] BPMN.io Logo in Kindern Click via MutationObserver unterbunden');
                  }, true);
                });
              }
            }
          });
        }
      });
    });

    // Observer starten
    observer.observe(canvasContainer, {
      childList: true,
      subtree: true
    });

    // Zusätzlich alle 500ms für 5 Sekunden prüfen (Fallback)
    let attempts = 0;
    const maxAttempts = 10; // 5 Sekunden bei 500ms Intervall

    const intervalCheck = setInterval(() => {
      attempts++;
      disableClicks();

      if (attempts >= maxAttempts) {
        clearInterval(intervalCheck);
        observer.disconnect();
        console.log('[ELIZA BPMN Viewer] Logo Click-Unterbindung Überwachung beendet');
      }
    }, 500);
  }

  /**
   * Mobile Touch-Unterstützung für Zoom und Pan
   */
  enableMobileControls() {
    const canvasContainer = this.canvas.getContainer();
    const svg = canvasContainer.querySelector('svg');

    if (!svg) return;

    // Touch-State Variablen
    let isTouch = false;
    let lastTouchDistance = 0;
    let lastTouchCenter = null;
    let lastSingleTouch = null;

    // Touch Start Handler
    const handleTouchStart = (e) => {
      isTouch = true;

      if (e.touches.length === 1) {
        // Single Touch für Pan
        const touch = e.touches[0];
        lastSingleTouch = {
          x: touch.clientX,
          y: touch.clientY
        };
      } else if (e.touches.length === 2) {
        // Pinch für Zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];

        lastTouchDistance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
        );

        lastTouchCenter = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2
        };

        // Single Touch für Pan deaktivieren
        lastSingleTouch = null;
      }
    };

    // Touch Move Handler
    const handleTouchMove = (e) => {
      e.preventDefault(); // Scrolling verhindern

      if (e.touches.length === 1 && lastSingleTouch) {
        // Single Touch Pan
        const touch = e.touches[0];
        const deltaX = touch.clientX - lastSingleTouch.x;
        const deltaY = touch.clientY - lastSingleTouch.y;

        // Canvas verschieben
        const viewbox = this.canvas.viewbox();
        this.canvas.viewbox({
          x: viewbox.x - deltaX,
          y: viewbox.y - deltaY,
          width: viewbox.width,
          height: viewbox.height
        });

        lastSingleTouch = {
          x: touch.clientX,
          y: touch.clientY
        };

      } else if (e.touches.length === 2 && lastTouchDistance > 0) {
        // Pinch Zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];

        const currentDistance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
        );

        const currentCenter = {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2
        };

        // Zoom-Faktor berechnen
        const zoomFactor = currentDistance / lastTouchDistance;
        const currentZoom = this.canvas.zoom();
        let newZoom = currentZoom * zoomFactor;

        // Zoom-Limits
        newZoom = Math.max(0.1, Math.min(4.0, newZoom));

        // Zoom anwenden
        this.canvas.zoom(newZoom, currentCenter);

        lastTouchDistance = currentDistance;
        lastTouchCenter = currentCenter;
      }
    };

    // Touch End Handler
    const handleTouchEnd = (e) => {
      if (e.touches.length === 0) {
        isTouch = false;
        lastSingleTouch = null;
        lastTouchDistance = 0;
        lastTouchCenter = null;
      } else if (e.touches.length === 1) {
        // Von Multi-Touch zu Single-Touch
        const touch = e.touches[0];
        lastSingleTouch = {
          x: touch.clientX,
          y: touch.clientY
        };
        lastTouchDistance = 0;
      }
    };

    // Event Listeners hinzufügen
    svg.addEventListener('touchstart', handleTouchStart, { passive: false });
    svg.addEventListener('touchmove', handleTouchMove, { passive: false });
    svg.addEventListener('touchend', handleTouchEnd, { passive: false });

    console.log('[ELIZA BPMN Viewer] Mobile Touch-Controls aktiviert');
  }
}

// Global verfügbar machen
window.ElizaBpmnViewer = ElizaBpmnViewer;

// Legacy-Kompatibilität für NavigatedViewer
window.BpmnJSViewer = BpmnViewer;

export default ElizaBpmnViewer;